export const sentiment = {
    id: 'sentiment',
    title: 'Sentiment and positioning',
    summary: 'Read how other clients are positioned, why a crowded trade is a risk to weigh, what risk-on and risk-off mean, and how each market moves with your own open trades.',
    level: 'advanced',
    art: 'eth',
    image: { src: '/academy/sentiment.webp', alt: 'An Ether coin above a split green and red gauge on a dark floor' },
    screen: { to: '/markets', label: 'Markets' },
    lessons: [
        {
            id: 'client-sentiment-long-short',
            title: 'Client sentiment: who is long and who is short',
            minutes: 4,
            level: 'advanced',
            blocks: [
                { type: 'p', text: '**Client sentiment** shows the share of traders holding an instrument on {brand} who are long. It counts open positions on live accounts only; demo accounts are left out.' },
                {
                    type: 'list',
                    items: [
                        'The **Sentiment** column on the Markets page shows a green bar for the long share and a red bar for the short share.',
                        'The quick view repeats it in words, with the number of traders.',
                        '**Our clients** in the market pulse gives the long share across all instruments.',
                        'It appears only once a minimum number of traders hold the instrument. Until then the column shows the wider market instead, drawn lighter and labelled with its source.',
                    ],
                },
                { type: 'example', title: 'Counting traders', text: '40 traders hold gold: 30 have a buy open and 10 have a sell open. Client sentiment shows **75% long**. A trader with both a buy and a sell open counts on both sides.' },
                { type: 'p', text: 'It counts people, not money. A client with a $50 trade counts the same as one with $50,000. And it covers {brand} clients only, not the whole market. Their trades do not set the price, which comes from outside price feeds.' },
                { type: 'p', text: 'Next to it, **Wider market** shows how traders across the much larger wider market are positioned in crypto, forex and gold. It is refreshed every 10 minutes and shows its change over 24 hours. Those traders are not {brand} clients, so compare the two rather than adding them up.' },
                { type: 'warn', text: 'Sentiment shows how clients are positioned, not where the price will go.' },
            ],
            quiz: [
                {
                    q: '20 traders hold a market: 5 long and 15 short. What does client sentiment show?',
                    options: ['25% long', '75% long', '5% long'],
                    answer: 0,
                    why: '5 ÷ (5 + 15) = 25% long.',
                },
                {
                    q: 'One client has a very large buy. How does that change the long share?',
                    options: ['It counts more than a small trade', 'It counts as one trader, like any other', 'It is left out'],
                    answer: 1,
                    why: 'Sentiment counts traders, not the size of their trades.',
                },
                {
                    q: 'Which accounts are counted?',
                    options: ['Live and demo', 'Live accounts only', 'Demo accounts only'],
                    answer: 1,
                    why: 'Only open positions on live accounts count toward client sentiment.',
                },
                {
                    q: 'An instrument shows “Wider market: 64% long”. Whose positions is that?',
                    options: ['{brand} clients', 'Traders in the wider market, outside {brand}', 'Every trader in the world'],
                    answer: 1,
                    why: 'The wider-market reading covers traders outside {brand}, not {brand} clients.',
                },
            ],
            tryIt: { to: '/markets', label: 'See client sentiment' },
            terms: ['client-sentiment', 'long', 'short'],
        },
        {
            id: 'crowded-trades-contrarian',
            title: 'Crowded trades and contrarian thinking',
            minutes: 5,
            level: 'advanced',
            blocks: [
                { type: 'p', text: 'A trade is **crowded** when most traders sit on the same side. The opportunity scanner on the Markets page has two screens for it: **Crowded long**, where at least 70% of traders are long, and **Crowded short**, where 30% or fewer are long.' },
                { type: 'p', text: 'A **contrarian** treats a crowded side as a reason for caution rather than confirmation. The idea: if most traders are already long, fewer buyers are left, and if the price turns, many of them may close at once.' },
                { type: 'p', text: 'That is a way of thinking, not a rule. Crowds are often right while a trend lasts, and a market can stay crowded for weeks. Here, sentiment covers {brand} clients only, so it is one small window on the market.' },
                { type: 'example', title: 'Two ways to read the same number', text: 'A coin has fallen 12% in a week and the scanner lists it as Crowded long: 78% of 60 traders are long. One reading: many clients are buying the dip. Another: if the fall continues, many of those buyers are holding losses and their stops sit below. Neither reading tells you what the price will do.' },
                { type: 'tip', text: 'If you are on the crowded side, ask what would make you wrong, and make sure your stop and size already allow for it.' },
                { type: 'warn', text: 'The scanner shows screens, not advice. Never open a trade just because a market appears on it.' },
            ],
            quiz: [
                {
                    q: 'When does a market appear under Crowded short?',
                    options: ['When 30% or fewer of its traders are long', 'When 70% or more are long', 'When the price falls 30%'],
                    answer: 0,
                    why: 'Crowded short means 30% or fewer of the traders holding it are long.',
                },
                {
                    q: 'A market is crowded long. What does that tell you for certain?',
                    options: ['The price will fall', 'Most clients holding it are long', 'The price will rise'],
                    answer: 1,
                    why: 'It describes positioning only. It is not a forecast in either direction.',
                },
            ],
            tryIt: { to: '/markets', label: 'Open the opportunity scanner' },
            terms: ['contrarian', 'client-sentiment', 'stop-loss'],
        },
        {
            id: 'risk-on-risk-off',
            title: 'Risk-on, risk-off',
            minutes: 4,
            level: 'advanced',
            blocks: [
                { type: 'p', text: 'Markets often swing between two moods. In a **risk-on** mood, traders are confident: shares, indices and crypto tend to rise. In a **risk-off** mood, they look for safety: gold and the Japanese yen tend to do better, and riskier markets fall.' },
                { type: 'p', text: 'The market pulse at the top of the Markets page shows a **Risk mood · 24h** reading. It takes the average 24-hour change of indices, stocks and crypto and subtracts the average change of gold and the yen. More than half a percentage point above zero reads **Risk-on**, more than half a point below reads **Risk-off**, anything between is **Neutral**.' },
                { type: 'example', title: 'Working out the mood', text: 'Indices, stocks and crypto are up 1.2% on average. Gold is up 0.2%. USD/JPY is up 0.4%, so the yen is down 0.4%. The safe side averages (0.2% − 0.4%) ÷ 2 = −0.1%. The score is 1.2% − (−0.1%) = **1.3 points**: Risk-on.' },
                { type: 'p', text: 'The yen is counted from USD/JPY turned upside down: when USD/JPY falls, the yen is getting stronger.' },
                { type: 'warn', text: 'The risk mood is a rough read of the last 24 hours, not a forecast. Moods can flip within a day on one piece of news.' },
            ],
            quiz: [
                {
                    q: 'Shares and crypto fall while gold and the yen rise. What is the mood?',
                    options: ['Risk-on', 'Risk-off', 'It cannot be read'],
                    answer: 1,
                    why: 'Money moving from riskier markets to gold and the yen is a risk-off move.',
                },
                {
                    q: 'USD/JPY falls 1%. What does that mean for the yen?',
                    options: ['The yen got stronger', 'The yen got weaker', 'Nothing'],
                    answer: 0,
                    why: 'Fewer yen per dollar means each yen is worth more.',
                },
            ],
            tryIt: { to: '/markets', label: 'Check the risk mood' },
            terms: ['risk-on-risk-off', 'asset-class'],
        },
        {
            id: 'vs-my-portfolio',
            title: 'Your portfolio’s correlation to the market',
            minutes: 5,
            level: 'advanced',
            blocks: [
                { type: 'p', text: 'The **Vs my portfolio** column on the Markets page shows how each instrument has moved with your open trades. Add it from **Columns** above the table.' },
                { type: 'p', text: 'It compares each instrument’s hourly price changes over the last 7 days with your open positions on the account you are using, weighted by their size. A sell counts the other way round from a buy. The result runs from **−1** (always opposite) through **0** (no link) to **+1** (always together).' },
                {
                    type: 'list',
                    items: [
                        '**Same bet**: +0.5 or more. A buy here adds to the risk you already hold.',
                        '**Related**: +0.2 to +0.5.',
                        '**Diversifies**: between −0.2 and +0.2. Little link to what you hold.',
                        '**Offsets**: −0.2 to −0.5.',
                        '**Hedges**: −0.5 or less. A buy here has tended to move against your current trades.',
                    ],
                },
                { type: 'example', title: 'One index trade', text: 'Your only trade is a $5,000 buy on the US Tech 100. The US 500 shows +0.92, Same bet: a buy there is close to a bigger version of the trade you have. Gold shows +0.05, Diversifies. If your US Tech 100 trade were a sell instead, the US 500 would show −0.92, Hedges.' },
                { type: 'p', text: 'The column assumes a buy. For a sell in an instrument, flip the sign: a sell in a Same bet market offsets your trades.' },
                { type: 'warn', text: 'Correlation over the last week can change next week. Use it to count related trades together, not to predict prices. It shows a dash when you have no open trades.' },
            ],
            quiz: [
                {
                    q: 'An instrument shows +0.85 in Vs my portfolio. What does a buy in it do?',
                    options: ['Adds to the risk you already hold', 'Protects your current trades', 'Nothing, it is unrelated'],
                    answer: 0,
                    why: 'A high positive value means it has moved with your trades, so a buy is close to the same bet.',
                },
                {
                    q: 'What does the column compare?',
                    options: ['Hourly price changes over the last 7 days with your open positions', 'Client sentiment with the price', 'Your closed trades with the index'],
                    answer: 0,
                    why: 'It uses each instrument’s hourly changes over 7 days against your open exposure.',
                },
                {
                    q: 'An instrument shows −0.7. You sell it. What does the sell do?',
                    options: ['It hedges your trades', 'It adds to the same bet', 'It has no effect'],
                    answer: 1,
                    why: 'The column assumes a buy. A sell flips the sign, so −0.7 becomes a same-direction bet.',
                },
            ],
            tryIt: { to: '/markets', label: 'Add the Vs my portfolio column' },
            terms: ['correlation-to-portfolio', 'correlation', 'exposure'],
        },
    ],
};
export const sentimentTerms = [
    { id: 'client-sentiment', term: 'Client sentiment', text: 'The share of traders holding an instrument on live accounts who are long. Shown once enough traders hold it. It counts people, not money, and is not a forecast. Beside it, the wider market shows the long share among traders outside {brand} (crypto, forex and gold).', lesson: 'client-sentiment-long-short' },
    { id: 'contrarian', term: 'Contrarian', text: 'A way of thinking that treats a crowded side as a reason for caution rather than confirmation. A view to weigh, not a rule.', lesson: 'crowded-trades-contrarian' },
    { id: 'risk-on-risk-off', term: 'Risk-on / risk-off', text: 'The market’s mood. Risk-on: shares, indices and crypto tend to rise. Risk-off: money moves toward gold and the yen. The Markets page shows a 24h reading.', lesson: 'risk-on-risk-off' },
    { id: 'correlation-to-portfolio', term: 'Vs my portfolio (correlation)', text: 'How an instrument’s hourly changes over the last 7 days have moved with your open trades, from −1 to +1. Assumes a buy; flip the sign for a sell.', lesson: 'vs-my-portfolio' },
];
//# sourceMappingURL=sentiment.js.map